import { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  EmailAuthProvider,
  linkWithCredential,
  deleteUser,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { auth, db, callFunction, functions } from "../firebase/config";
import toast from "react-hot-toast";
import { httpsCallable } from "firebase/functions";

const AuthContext = createContext({});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          console.log(firebaseUser, "firebase");
          setUser(firebaseUser);

          // Get additional user data from Firestore
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData({
              id: firebaseUser.uid,
              ...data,
            });

            // Update last active
            updateDoc(userDocRef, {
              lastActive: serverTimestamp(),
            }).catch(console.error);
          } else {
            // User exists in Auth but not Firestore - shouldn't happen
            console.error("User document not found");
            setUser(null);
            setUserData(null);
          }
        } else {
          setUser(null);
          setUserData(null);
        }
      } catch (error) {
        console.error("Auth state change error:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // const setAdmin = async (uid) => {
  //   try {
  //     const setAdminClaim = httpsCallable(functions, "setAdminClaim");
  //     const result = await setAdminClaim({ uid }); // send uid as payload
  //     console.log("Function response:", result.data);
  //   } catch (error) {
  //     console.error("Error calling setAdminClaim:", error);
  //   }
  // };

  // Set up invisible ReCaptcha for phone verification
  const setUpRecaptcha = (number, recaptchaContainerId) => {
    try {
      const recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
        size: "invisible",
      });
      return signInWithPhoneNumber(auth, number, recaptchaVerifier);
    } catch (err) {
      console.error("Recaptcha error:", err);
      throw err;
    }
  };

  // Atomic Registration: Phase 2 (Link Email & Finalize)
  const linkAndFinalize = async (email, password, additionalData = {}) => {
    if (!auth.currentUser) {
      throw new Error("No authenticated session found. Please verify your phone first.");
    }
 
    const firebaseUser = auth.currentUser;
    const credential = EmailAuthProvider.credential(email, password);
 
    try {
      // 1. Link Email/Password to the Phone User
      console.log("Linking email to user:", firebaseUser.uid);
      const linkedCredential = await linkWithCredential(firebaseUser, credential);
      const finalUser = linkedCredential.user;
 
      // 2. Create Firestore Document
      const userData = {
        uid: finalUser.uid,
        email: email.toLowerCase(),
        displayName: email.split("@")[0],
        telegramUsername: additionalData.telegramUsername || null,
        phone: finalUser.phoneNumber,
        dateOfBirth: additionalData.dateOfBirth || null,
        stateOfResidence: additionalData.stateOfResidence || null,
        minimumAgeRequired: additionalData.minimumAgeRequired || 18,
        ageVerifiedAtRegistration: true,
        deviceId: additionalData.deviceId || null,
        // AML/KYC Policy v1.0 §4.2 / §14: server-computed device-binding
        // hash over IP + UA + accept-language + coarse browser/hardware
        // signals. Dedup'd at registration by checkPreRegistrationSybil.
        signalHash: additionalData.signalHash || null,
        phoneVerified: true,
        totalEntries: 0,
        totalWins: 0,
        totalPrizeAmount: 0,
        averageAnswerTime: 0,
        fastestAnswerTime: 0,
        dailyTriviaAttempts: additionalData.telegramUsername ? 10 : 5,
        lastTriviaReset: serverTimestamp(),
        hasTelegram: !!additionalData.telegramUsername,
        notifications: {
          email: true,
          telegram: !!additionalData.telegramUsername,
          sms: false,
        },
        isAdmin: false,
        isBanned: false,
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp(),
      };
 
      await setDoc(doc(db, "users", finalUser.uid), userData);
 
      // 3. Referral processing (non-blocking)
      try {
        if (additionalData.referredBy) {
          await callFunction("applyReferralCode", { referralCode: additionalData.referredBy });
        }
        await callFunction("getMyReferralCode", {});
      } catch (refError) {
        console.warn("Referral processing failed (non-blocking):", refError.message);
      }
 
      await refreshUserData();
      toast.success("Account created successfully!");
      return { success: true, user: finalUser };
    } catch (error) {
      console.error("Linking/Finalization failed:", error);
      
      // CLEANUP: If linking failed, we delete the temporary phone user
      // so we don't end up with "Phone-only" ghost accounts
      try {
        console.warn("Cleaning up temporary phone account due to registration failure...");
        await deleteUser(firebaseUser);
      } catch (deleteError) {
        console.error("Cleanup failed:", deleteError);
      }
 
      let errorMessage = error.message;
      if (error.code === "auth/email-already-in-use" || error.code === "auth/credential-already-in-use") {
        errorMessage = "This email is already in use. Please use a different email or log in.";
      }
      
      throw new Error(errorMessage);
    }
  };
 
  // Simplified Register (Phase 1: Sybil check only)
  const register = async (email, password, additionalData = {}) => {
    // This now just does the Sybil check before the UI starts the OTP flow
    const checkPreRegistrationSybil = httpsCallable(functions, "checkPreRegistrationSybil");
    const result = await checkPreRegistrationSybil({
      deviceId: additionalData.deviceId || null,
      phone: additionalData.phone || null,
      signals: additionalData.signals || null,
    });

    if (result.data && result.data.allowed === false) {
      throw new Error(result.data.reason || "Registration blocked by Anti-Sybil protections.");
    }

    // Cache the server-computed signalHash so linkAndFinalize can
    // persist it on the user document (AML/KYC §4.2 / §14).
    return { success: true, signalHash: result.data?.signalHash || null };
  };

  // Login existing user
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const firebaseUser = userCredential.user;

      // Update login stats
      const userRef = doc(db, "users", firebaseUser.uid);
      await updateDoc(userRef, {
        lastActive: serverTimestamp(),
        loginCount: increment(1),
      });

      // Get user data
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      setUser(firebaseUser);
      setUserData(
        userDoc.exists()
          ? {
              id: firebaseUser.uid,
              ...userData,
            }
          : null,
      );

      // Check if user is banned
      if (userData?.isBanned) {
        await signOut(auth);
        throw new Error(
          `Account banned: ${userData.banReason || "Terms violation"}`
        );
      }

      toast.success("Welcome back!");

      return {
        success: true,
        user: firebaseUser,
        userData: { id: firebaseUser.uid, ...userData },
      };
    } catch (error) {
      console.error("Login error:", error);

      let errorMessage = error.message;
      if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/wrong-password"
      ) {
        errorMessage = "Invalid email or password";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many failed attempts. Please try again later.";
      }

      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout user
  const logout = async () => {
    try {
      await signOut(auth);
      toast.success("Logged out successfully");
      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to logout");
      throw new Error("Failed to logout");
    }
  };

  // Send password reset email
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success("Password reset email sent! Check your inbox.");
      return { success: true };
    } catch (error) {
      console.error("Password reset error:", error);

      let errorMessage = "Failed to send reset email";
      if (error.code === "auth/user-not-found") {
        errorMessage = "No account found with this email";
      }

      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    if (!user) throw new Error("No user logged in");

    try {
      // Update auth profile if display name changed
      if (updates.displayName && updates.displayName !== user.displayName) {
        await updateProfile(user, { displayName: updates.displayName });
      }

      // Update Firestore document
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      // Refresh user data
      await refreshUserData();

      toast.success("Profile updated successfully");
      return { success: true };
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Failed to update profile");
      throw error;
    }
  };

  // Add Telegram to account
  const addTelegramToAccount = async (telegramUsername) => {
    if (!user) throw new Error("No user logged in");
 
    try {
      const result = await callFunction("addTelegramToAccount", {
        telegramUsername,
        userId: user?.uid,
      });
 
      if (result.success) {
        await refreshUserData();
        toast.success("Telegram added successfully!");
        return result;
      } else {
        throw new Error(result.error || "Failed to add Telegram");
      }
    } catch (error) {
      console.error("Add Telegram error:", error);
      toast.error(error.message);
      throw error;
    }
  };
 
  // Link phone number to existing email account
  // const linkPhone = async (otp) => {
  //   if (!auth.currentUser || !window.confirmationResult) {
  //     throw new Error("Phone verification session not found. Please try again.");
  //   }
 
  //   try {
  //     const result = await window.confirmationResult.confirm(otp);
  //     const linkedUser = result.user;
 
  //     // Update Firestore
  //     const userRef = doc(db, "users", linkedUser.uid);
  //     await updateDoc(userRef, {
  //       phoneVerified: true,
  //       phone: linkedUser.phoneNumber,
  //       updatedAt: serverTimestamp(),
  //     });
 
  //     await refreshUserData();
  //     toast.success("Phone number linked successfully!");
  //     return { success: true, user: linkedUser };
  //   } catch (error) {
  //     console.error("Link phone error:", error);
  //     let message = "Invalid verification code.";
  //     if (error.code === "auth/credential-already-in-use") {
  //       message = "This phone number is already linked to another account.";
  //     }
  //     toast.error(message);
  //     throw new Error(message);
  //   }
  // };

  // Refresh user data from Firestore
  const refreshUserData = async () => {
    if (!user) return;

    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        setUserData({
          id: user.uid,
          ...userDoc.data(),
        });
      }
    } catch (error) {
      console.error("Refresh user data error:", error);
    }
  };

  // Get user stats
  const getUserStats = async () => {
    if (!user) return null;

    try {
      // Get user's squares
      const squaresQuery = query(
        collection(db, "squareClaims"),
        where("userId", "==", user.uid)
      );
      const squaresSnapshot = await getDocs(squaresQuery);

      // Get user's wins
      const winsQuery = query(
        collection(db, "winners"),
        where("userId", "==", user.uid)
      );
      const winsSnapshot = await getDocs(winsQuery);

      let totalPrizeAmount = 0;
      winsSnapshot.forEach((doc) => {
        totalPrizeAmount += doc.data().prizeAmount || 0;
      });

      return {
        totalSquares: squaresSnapshot.size,
        totalWins: winsSnapshot.size,
        totalPrizeAmount,
        averageAnswerTime: userData?.averageAnswerTime || 0,
        fastestAnswerTime: userData?.fastestAnswerTime || 0,
      };
    } catch (error) {
      console.error("Get user stats error:", error);
      return null;
    }
  };

  // Helper: refresh user data including backend-generated referral code
  // async function loadCanonicalReferralCode(uid) {
  //   try {
  //     const userDoc = await getDoc(doc(db, "users", uid));
  //     if (userDoc.exists()) {
  //       setUserData({ id: uid, ...userDoc.data() });
  //     }
  //   } catch (err) {
  //     console.error("Failed to refresh user data:", err);
  //   }
  // }

  const value = {
    user,
    userData,
    loading,
    error,
    // Auth functions
    register,
    linkAndFinalize,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    addTelegramToAccount,
    setUpRecaptcha,
    // Data functions
    refreshUserData,
    getUserStats,
    // Helper
    isAdmin: userData?.isAdmin || false,
    hasTelegram: userData?.hasTelegram || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
